import { useMemo } from 'react';

import type { ColDef } from 'ag-grid-community';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import { IconSettings } from '@tabler/icons-react';
import {
  Iconsvg,
  Tag,
  Tooltip,
  TooltipBody,
} from '@wk/components-react16';

import type {
  OnboardingCheckpointListProps,
  OnboardingCheckpointViewModel,
} from '@/features/onboarding/types/onboarding.types';

import OnboardingExceptionApprovalTag from './OnboardingExceptionApprovalTag';

const OnboardingCheckpointGrid = <TCheckpoint,>({
  checkpoints,
  onConfigureCheckpoint,
}: OnboardingCheckpointListProps<TCheckpoint>) => {
  const columnDefs = useMemo<
    Array<ColDef<OnboardingCheckpointViewModel<TCheckpoint>>>
  >(
    () => [
      {
        headerName: 'Checkpoint Id',
        field: 'id',
        minWidth: 400,
        cellRenderer: (
          params: CustomCellRendererProps<
            OnboardingCheckpointViewModel<TCheckpoint>
          >,
        ) => {
          if (!params.data) {
            return null;
          }

          return (
            <span className="flex items-center gap-2 max-sm:flex-wrap">
              {params.data.requiresParameters ? (
                <>
                  <span
                    id={`checkpoint-requires-parameters-${params.data.key}`}
                    className="flex items-center text-[#ea8f00]"
                  >
                    <Iconsvg name="caution" isFill size={12} />
                  </span>
                  <Tooltip
                    position="right"
                    targetSelector={`#checkpoint-requires-parameters-${params.data.key}`}
                  >
                    <TooltipBody slot="tooltipBody">
                      This checkpoint requires parameters.
                    </TooltipBody>
                  </Tooltip>
                </>
              ) : null}
              <span className="h-full">{params.data.id}</span>
              {params.data.isNew ? <Tag color="green">New</Tag> : null}
              {params.data.isPreview ? (
                <>
                  <span
                    id={`checkpoint-preview-${params.data.key}`}
                    className="flex h-full items-center"
                  >
                    <Tag color="gray" contrastMode="light">
                      Preview
                    </Tag>
                  </span>
                  <Tooltip
                    position="right"
                    targetSelector={`#checkpoint-preview-${params.data.key}`}
                  >
                    <TooltipBody slot="tooltipBody">
                      This checkpoint is not scored since it is in preview.
                      <br /> It will be included in an upcoming release.
                    </TooltipBody>
                  </Tooltip>
                </>
              ) : null}
              <OnboardingExceptionApprovalTag
                approvalStatus={params.data.exceptionApprovalStatus}
              />
            </span>
          );
        },
      },
      {
        headerName: 'Checkpoint Version',
        field: 'versionLabel',
        minWidth: 200,
      },
      {
        headerName: 'Checkpoint Description',
        field: 'description',
        minWidth: 450,
      },
      {
        headerName: 'Category',
        field: 'category',
      },
      {
        headerName: 'Accountable',
        field: 'accountable',
      },
      {
        headerName: 'Actions',
        minWidth: 150,
        colId: 'actions',
        pinned: 'right',
        cellRenderer: (
          params: CustomCellRendererProps<
            OnboardingCheckpointViewModel<TCheckpoint>
          >,
        ) => {
          if (!params.data) {
            return null;
          }

          return (
            <>
              <span className="flex h-full w-full cursor-pointer items-center justify-center">
                <IconSettings
                  id={`configure-checkpoint-${params.data.key}`}
                  size={16}
                />
              </span>
              <Tooltip
                position="left"
                targetSelector={`#configure-checkpoint-${params.data.key}`}
              >
                <TooltipBody slot="tooltipBody">Configure</TooltipBody>
              </Tooltip>
            </>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="subject-onboarding-grid-container h-[500px] cg-ag-grid-field ag-theme-balham">
      <AgGridReact
        autoSizeStrategy={{
          type: 'fitGridWidth',
          defaultMinWidth: 250,
        }}
        onCellClicked={(event) => {
          if (event.data) {
            onConfigureCheckpoint(event.data.rawCheckpoint);
          }
        }}
        rowData={checkpoints}
        columnDefs={columnDefs}
      />
    </div>
  );
};

export default OnboardingCheckpointGrid;
